// What this TV is displaying, persisted per hospital so a rebooted screen
// comes straight back up without anyone touching it.
//
// Two sources, in priority order:
//   1. A TV profile id — the normal path. Everything else (doctors, layout,
//      fields) is read from the server, so edits made in the dashboard reach
//      the screen on its next load.
//   2. A QueueV2Selection — the manual /v2/setup escape hatch, for a hospital
//      with no profiles yet or a one-off on-site override.
import { isAnnouncementLanguage } from "./audioLanguage";

export type ViewMode = "column" | "slideshow";

export interface QueueV2Selection {
  mappingIds: string[];
  viewMode: ViewMode;
  // Page size is fixed by mode (slideshow = 1 doctor, column = 2). Only the
  // rotation cadence is configurable.
  intervalSec: number; // seconds per page before it rotates
  ads: boolean; // show the right-side ad/resource panel
  // Spoken-announcement language. A TV profile carries its own `language`, so
  // this is the manual path's equivalent — without it a hand-configured screen
  // just inherits whatever audioLanguage was last written to this browser.
  language: string;
}

export const DEFAULT_SELECTION: Omit<QueueV2Selection, "mappingIds"> = {
  viewMode: "column",
  intervalSec: 10,
  ads: false,
  language: "en",
};

const storageKey = (hospitalId: string) => `queueV2Selection_${hospitalId}`;
const profileKey = (hospitalId: string) => `queueV2Profile_${hospitalId}`;
const prefsKey = (hospitalId: string) => `queueV2ScreenPrefs_${hospitalId}`;

// Rotation cadence and the ad panel. A TV profile has no columns for these
// yet, and they're arguably per-SCREEN anyway — a lobby TV and a corridor TV
// running the same profile can reasonably differ — so they sit beside the
// profile id rather than inside it.
//
// This is NOT the localStorage-beats-dashboard pattern we just deleted: there
// is no server value for these to override. If the profile later gains the
// columns, that value becomes the source and this drops to an explicit
// per-screen override.
export interface ScreenPrefs {
  intervalSec: number;
  ads: boolean;
}

export const DEFAULT_SCREEN_PREFS: ScreenPrefs = {
  intervalSec: 10,
  ads: false,
};

export const saveScreenPrefs = (
  hospitalId: string,
  prefs: ScreenPrefs
): void => {
  try {
    localStorage.setItem(prefsKey(hospitalId), JSON.stringify(prefs));
  } catch (err) {
    console.error("[queue-v2] failed to persist screen prefs:", err);
  }
};

export const loadScreenPrefs = (hospitalId: string): ScreenPrefs => {
  try {
    const raw = localStorage.getItem(prefsKey(hospitalId));
    if (!raw) return DEFAULT_SCREEN_PREFS;
    const parsed = JSON.parse(raw);
    return {
      intervalSec:
        typeof parsed?.intervalSec === "number" && parsed.intervalSec >= 3
          ? parsed.intervalSec
          : DEFAULT_SCREEN_PREFS.intervalSec,
      ads: parsed?.ads === true,
    };
  } catch (err) {
    console.error("[queue-v2] failed to read screen prefs:", err);
    return DEFAULT_SCREEN_PREFS;
  }
};

// Only the id is stored — never the profile's contents. That is the whole
// point: the dashboard stays the source of truth, and the TV re-reads it.
export const saveProfileId = (hospitalId: string, profileId: string): void => {
  try {
    localStorage.setItem(profileKey(hospitalId), profileId);
    // The two sources are mutually exclusive; drop any stale manual setup so
    // it can't win on the next load.
    localStorage.removeItem(storageKey(hospitalId));
  } catch (err) {
    console.error("[queue-v2] failed to persist profile id:", err);
  }
};

export const loadProfileId = (hospitalId: string): string | null => {
  try {
    return localStorage.getItem(profileKey(hospitalId));
  } catch (err) {
    console.error("[queue-v2] failed to read profile id:", err);
    return null;
  }
};

export const clearProfileId = (hospitalId: string): void => {
  try {
    localStorage.removeItem(profileKey(hospitalId));
  } catch (err) {
    console.error("[queue-v2] failed to clear profile id:", err);
  }
};

export const saveSelection = (
  hospitalId: string,
  selection: QueueV2Selection
): void => {
  try {
    localStorage.setItem(storageKey(hospitalId), JSON.stringify(selection));
    // Manual setup overrides whichever profile was picked before.
    localStorage.removeItem(profileKey(hospitalId));
  } catch (err) {
    console.error("[queue-v2] failed to persist selection:", err);
  }
};

export const loadSelection = (
  hospitalId: string
): QueueV2Selection | null => {
  try {
    const raw = localStorage.getItem(storageKey(hospitalId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed?.mappingIds)) return null;
    return {
      mappingIds: parsed.mappingIds,
      viewMode: parsed.viewMode === "slideshow" ? "slideshow" : "column",
      intervalSec:
        typeof parsed.intervalSec === "number" && parsed.intervalSec >= 3
          ? parsed.intervalSec
          : DEFAULT_SELECTION.intervalSec,
      ads: parsed.ads === true,
      // Validated, not just type-checked: an unrecognised code would leave the
      // setup screen's <select> rendering blank, and the backend would speak
      // English anyway.
      language: isAnnouncementLanguage(parsed.language)
        ? parsed.language
        : DEFAULT_SELECTION.language,
    };
  } catch (err) {
    console.error("[queue-v2] failed to read selection:", err);
    return null;
  }
};
